import http from "./http";

/**
 * Service for the user notification center + bell (Trello #264).
 *
 * Uses the central http instance — no raw axios, no module-scope token reads.
 * Shared by NotificationBadgeDashboard.vue (bell) and NotificacionesCentroView.vue (center).
 */
const NotificacionesService = {
  /**
   * Fetches a page of the current user's notifications.
   * @param {object} [params] - { offset=0, limit=50 }
   * @returns {Promise} Axios response; response.data = { notificaciones, total_no_leidas, total, offset, limit }
   */
  getNotificaciones({ offset = 0, limit = 50 } = {}) {
    return http.get("notificaciones/usuario", { params: { offset, limit } });
  },

  /**
   * Marks a list of notifications as read. Backend contract
   * (updateNotificacionesUsuario) reads req.body.notificaciones as an array
   * of { id_notificacion }, not a plain array of ids.
   * @param {number[]} ids - id_notificacion values to mark as visto
   * @returns {Promise} Axios response with the standard toast envelope
   */
  marcarVistos(ids) {
    return http.put("notificaciones/usuario", {
      notificaciones: ids.map((id) => ({ id_notificacion: id })),
    });
  },

  /**
   * Marks every unread notification for the current user as read.
   * @returns {Promise} Axios response with the standard toast envelope
   */
  marcarTodosVistos() {
    return http.put("notificaciones/usuario/visto-todos");
  },

  /**
   * Deletes the current user's own notification row. Backend returns 404
   * when the notification does not belong to the authenticated user.
   * @param {number} id - id_notificacion
   * @returns {Promise} Axios response with the standard toast envelope
   */
  eliminar(id) {
    return http.delete(`notificaciones/usuario/${id}`);
  },

  /**
   * Lists the full tipo -> roles notification matrix. Restricted server-side
   * to roles authorized on 'notificaciones/configuracion'.
   * @returns {Promise} response.data = { config: [{ id_config, tipo, descripcion, id_roles, activo, tiene_emisores }] }
   */
  getConfig() {
    return http.get("notificaciones/config");
  },

  /**
   * Updates one notification type's role matrix / active flag. Route key is
   * the type string (`tipo`), not id_config — confirmed backend contract.
   * @param {string} tipo
   * @param {object} payload - { id_roles, activo }
   * @returns {Promise} response.data = { title, msg, type, config }
   */
  actualizarConfig(tipo, payload) {
    return http.put(`notificaciones/config/${tipo}`, payload);
  },

  /**
   * Returns which roles administer the config panel (config_roles) and
   * which roles see the notification center (centro_roles).
   * @returns {Promise} response.data = { centro_roles, config_roles }
   */
  getAcceso() {
    return http.get("notificaciones/acceso");
  },

  /**
   * Updates centro/config role access. The backend rejects the request if
   * config_roles omits role 1 (superAdmin, anti-lockout) or contains
   * non-existent roles. Effective for affected users on their NEXT LOGIN.
   * @param {object} payload - { centro_roles, config_roles }
   * @returns {Promise} response.data = { title, msg, type, acceso }
   */
  actualizarAcceso(payload) {
    return http.put("notificaciones/acceso", payload);
  },

  /**
   * Lists every role for the config/access role pickers. Reuses the existing
   * usuarios/roles endpoint through the central http instance (never raw
   * axios, unlike the legacy usage in Usuarios.vue / DialogCreate.vue).
   * @returns {Promise} response.data = [{ id_rol, rol }]
   */
  getRoles() {
    return http.get("usuarios/roles");
  },
};

export default NotificacionesService;
